import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import type { RbacPermission } from '@digitaltattoo/types';
import type { RootState } from '../store';
import { getMyPermissions } from '../services/rbacApi';

interface RbacContextValue {
  permissions: readonly RbacPermission[];
  loading: boolean;
  can: (permission: RbacPermission) => boolean;
  refresh: () => Promise<void>;
}

const RbacContext = createContext<RbacContextValue | null>(null);

export function RbacProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const role = String(user?.role || '').trim().toLowerCase().replace(/-/g, '_');
  const [permissions, setPermissions] = useState<readonly RbacPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedRole, setLoadedRole] = useState<string | null>(null);
  const requiresLoad = Boolean(
    isAuthenticated && user?.userType !== 'PRACTITIONER' && role,
  );

  const refresh = useCallback(async () => {
    if (!isAuthenticated || user?.userType === 'PRACTITIONER' || !role) {
      setPermissions([]);
      setLoadedRole(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getMyPermissions();
      setPermissions(data.permissions);
    } catch (error) {
      console.error('Failed to refresh RBAC permissions:', error);
      // Fail closed in the UI. The API remains the authorization source of truth.
      setPermissions([]);
    } finally {
      setLoadedRole(role);
      setLoading(false);
    }
  }, [isAuthenticated, role, user?.userType]);

  useEffect(() => {
    setPermissions([]);
    void refresh();
  }, [role, isAuthenticated, refresh]);

  const value = useMemo<RbacContextValue>(
    () => ({
      permissions,
      loading: loading || (requiresLoad && loadedRole !== role),
      can: (permission) => permissions.includes(permission),
      refresh,
    }),
    [permissions, loading, requiresLoad, loadedRole, role, refresh],
  );

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
}

export function useRbac(): RbacContextValue {
  const context = useContext(RbacContext);
  if (!context) {
    throw new Error('useRbac must be used inside RbacProvider');
  }
  return context;
}
